import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import * as QuoteActions from './quote.actions';
import { QuoteService } from '../../services/quote.service';

@Injectable()
export class QuoteEffects {
  private actions$ = inject(Actions);
  private quoteService = inject(QuoteService);

  private getErrorMessage(error: unknown): string {
    console.error('Quote API Error:', error);
    if (error instanceof HttpErrorResponse) {
      console.error('Error details:', error.error);
      if (error.status === 0) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      if (error.status === 400) {
        if (typeof error.error === 'string') {
          return error.error;
        }
        if (error.error?.message) {
          return error.error.message;
        }
        if (error.error?.errors) {
          const errors = Object.values(error.error.errors).flat().join(' ');
          return errors || 'Please check your information and try again.';
        }
        return 'Please check your information and try again.';
      }
      if (error.status === 500) {
        if (error.error?.message) {
          return `Server error: ${error.error.message}`;
        }
        return 'Server error. Please try again later.';
      }
      return error.error?.message || error.message || 'An error occurred. Please try again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  submitQuoteApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.submitQuoteApplication),
      mergeMap(({ data }) =>
        this.quoteService.submitApplication(data).pipe(
          map(application => QuoteActions.submitQuoteApplicationSuccess({ application })),
          catchError(error =>
            of(QuoteActions.submitQuoteApplicationFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getQuoteApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.getQuoteApplications),
      mergeMap(({ pageNumber = 1, pageSize = 10 }) =>
        this.quoteService.getApplications(pageNumber, pageSize).pipe(
          map(page => QuoteActions.getQuoteApplicationsSuccess({ page })),
          catchError(error =>
            of(QuoteActions.getQuoteApplicationsFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getQuoteApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuoteActions.getQuoteApplicationById),
      mergeMap(({ id }) =>
        this.quoteService.getApplicationById(id).pipe(
          map(application => QuoteActions.getQuoteApplicationByIdSuccess({ application })),
          catchError(error =>
            of(QuoteActions.getQuoteApplicationByIdFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );
}